// Fase 10 (POS) — catálogo maestro de productos.
//
// Independiente de Producción: cualquier negocio que use SIGEPRO puede dar
// de alta aquí sus productos, así no fabrique nada (solo revenda mercancía
// comprada). Un fabricante normalmente no necesita usar esta pantalla para
// el alta inicial — el producto se crea solo la primera vez que se cierra un
// resultado de producción con ese nombre (ver produccion_routes.py) — pero
// sí la usa para parametrizar la unidad de venta y el código de barras, o
// para dar de alta un producto antes de producirlo por primera vez.
import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Icon } from "../icons";

type Producto = { id: number; nombre: string; unidadVenta: "unidad" | "peso"; codigoBarras: string | null; activo: boolean };

const UNIDAD_LABEL: Record<string, string> = { unidad: "Por unidad", peso: "Por peso (gramos)" };

export function Productos() {
  const { tienePermiso } = useAuth();
  const puedeEditar = tienePermiso("core.catalogos:editar");

  const [productos, setProductos] = useState<Producto[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");

  const [modal, setModal] = useState<Producto | "nuevo" | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      setProductos(await api.get("/productos"));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar el catálogo de productos.");
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  const toggleActivo = async (p: Producto) => {
    try {
      await api.put(`/productos/${p.id}`, { activo: !p.activo });
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo actualizar el producto.");
    }
  };

  const eliminar = async (p: Producto) => {
    if (!window.confirm(`¿Eliminar "${p.nombre}" del catálogo de productos?`)) return;
    try {
      await api.del(`/productos/${p.id}`);
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo eliminar el producto.");
    }
  };

  const visibles = productos.filter((p) => p.nombre.toLowerCase().includes(busqueda.trim().toLowerCase()));

  return (
    <Shell titulo="Productos" breadcrumb="Core / Productos">
      <div className="flex items-center justify-between mb-4 gap-3">
        <p className="text-sm text-textmuted">
          Unidad de venta y código de barras por producto — de aquí los toma el mostrador (POS) para saber si
          vender por unidad o por peso, y para reconocer un código escaneado. Un producto nuevo también se crea
          solo al cerrar el primer resultado de producción con ese nombre; aquí se ajustan sus atributos.
        </p>
        {puedeEditar && <button className="btn-primary shrink-0" onClick={() => setModal("nuevo")}>+ Nuevo producto</button>}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Icon name="search" className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-textmuted" />
        <input className="input-field pl-9" placeholder="Buscar producto..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      <div className="card overflow-hidden">
        {cargando ? (
          <p className="p-6 text-sm text-textmuted">Cargando...</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-borderc">
                <th className="table-th">Nombre</th>
                <th className="table-th">Unidad de venta</th>
                <th className="table-th">Código de barras (EAN-13)</th>
                <th className="table-th">Estado</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((p) => (
                <tr key={p.id} className="border-b border-borderc last:border-0">
                  <td className="table-td font-medium text-slate-800">{p.nombre}</td>
                  <td className="table-td">
                    <span className={`badge ${p.unidadVenta === "peso" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                      {UNIDAD_LABEL[p.unidadVenta]}
                    </span>
                  </td>
                  <td className="table-td font-mono text-xs">{p.codigoBarras || <span className="text-textmuted">—</span>}</td>
                  <td className="table-td">
                    <span className={`badge ${p.activo ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>
                      {p.activo ? "activo" : "inactivo"}
                    </span>
                  </td>
                  <td className="table-td text-right space-x-2">
                    {puedeEditar && (
                      <>
                        <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => setModal(p)}>Editar</button>
                        <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => toggleActivo(p)}>
                          {p.activo ? "Desactivar" : "Activar"}
                        </button>
                        <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => eliminar(p)}>Eliminar</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {visibles.length === 0 && (
                <tr><td colSpan={5} className="table-td text-center text-textmuted py-8">
                  {productos.length === 0 ? "Aún no hay productos en el catálogo." : "Ningún producto coincide con la búsqueda."}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {modal && <ModalProducto producto={modal === "nuevo" ? null : modal} onClose={() => setModal(null)} onGuardado={() => { setModal(null); cargar(); }} />}
    </Shell>
  );
}

function ModalProducto({ producto, onClose, onGuardado }: { producto: Producto | null; onClose: () => void; onGuardado: () => void }) {
  const [nombre, setNombre] = useState(producto?.nombre || "");
  const [unidadVenta, setUnidadVenta] = useState<"unidad" | "peso">(producto?.unidadVenta || "unidad");
  const [codigoBarras, setCodigoBarras] = useState(producto?.codigoBarras || "");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codigoInvalido = codigoBarras.trim().length > 0 && !/^\d{13}$/.test(codigoBarras.trim());

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (codigoInvalido) return;
    setGuardando(true);
    setError(null);
    const body = { nombre, unidadVenta, codigoBarras: codigoBarras.trim() || null };
    try {
      if (producto) await api.put(`/productos/${producto.id}`, body);
      else await api.post("/productos", body);
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo guardar el producto.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={guardar} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">{producto ? "Editar producto" : "Nuevo producto"}</h3>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre *</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required autoFocus />
          {producto && (
            <p className="text-[10px] text-textmuted mt-1">
              Si lo renombra, tenga en cuenta que el inventario, los precios y las ventas ya registradas siguen
              vinculados por el nombre anterior — el histórico no cambia solo.
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Unidad de venta</label>
          <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-surface p-1">
            <button type="button" onClick={() => setUnidadVenta("unidad")}
              className={`rounded-md py-2 text-xs font-semibold transition-colors ${unidadVenta === "unidad" ? "bg-white text-accent-dark shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Por unidad
            </button>
            <button type="button" onClick={() => setUnidadVenta("peso")}
              className={`rounded-md py-2 text-xs font-semibold transition-colors ${unidadVenta === "peso" ? "bg-white text-accent-dark shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Por peso (gramos)
            </button>
          </div>
          <p className="text-[10px] text-textmuted mt-1">
            {unidadVenta === "peso"
              ? "En el mostrador se pedirá el peso vendido en gramos en vez de una cantidad entera; el precio sigue siendo por kilogramo, como en el resto de SIGEPRO."
              : "En el mostrador se agrega y se cuenta como unidades enteras (1, 2, 3...)."}
          </p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Código de barras (EAN-13, opcional)</label>
          <input
            className="input-field font-mono"
            inputMode="numeric"
            maxLength={13}
            value={codigoBarras}
            onChange={(e) => setCodigoBarras(e.target.value.replace(/\D/g, ""))}
            placeholder="7701234567890"
          />
          {codigoInvalido && <p className="text-[11px] text-red-600 mt-1">Debe tener exactamente 13 dígitos.</p>}
          <p className="text-[10px] text-textmuted mt-1">Permite agregarlo al carrito escaneándolo en el mostrador.</p>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={guardando || codigoInvalido}>{guardando ? "Guardando..." : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}
